import { useEffect, useState, useMemo } from 'react';
import { readStoredSession, clearStoredSession } from './auth/PinScreen';
import { setActor, setJwt, aws } from './lib/awsdb';
import Finance from './finance/Finance';
import B2B from './b2b/B2B';
import D2CSales from './d2c/D2CSales';
import AddressBook from './contacts/AddressBook';
import VaultPage from './vault/VaultPage';
import WebChanges from './webchanges/WebChanges';
import Archive from './archive/Archive';
import Forecaster from './forecaster/Forecaster';
import AdminPanel from './admin/AdminPanel';
import SystemSignalsModal from './admin/SystemSignalsModal';
import FuturisticLogo from './components/FuturisticLogo';
import NebulaLandingAuth from './components/NebulaLandingAuth/NebulaLandingAuth';
import Signals from './signals/Signals';
import RetentionTab from './retention/Retention';
import Welcome from './onboarding/Welcome';
import Sales from './sales/Sales';
import Home from './home/Home';
import Reporting from './reporting/Reporting';
import Inbox from './inbox/Inbox';
import Clouseau from './clouseau/Clouseau';
import CalendarPage from './calendar/Calendar';
import CommandPalette from './ui/CommandPalette';
import FilesTab from './files/Files';
import { NavBar } from './components/nav/NavBar';
import { ToastProvider } from './lib/Toast';
import HealthBanner from './lib/HealthBanner';
import { PageContextProvider } from './lib/PageContext';

// Right-anchored editorial nav (replaces the legacy left sidebar).
//
// 2026-05-08 cleanup: removed Cases / Meetings / Creative / B2B / Ads / News /
// Support nav entries — the surfaces and their backing tables/Lambdas were
// dropped in this same change. Cmd+M was disabled in the same pass; Slack
// is now the only way to talk to Harry.
const NB_VIEW_FROM_ROUTE = {
  home: 'home',
  stock: 'forecaster',
  finance: 'finance',
  b2b: 'b2b',
  contacts: 'contacts',
  reporting: 'reporting',
  files: 'files',
  calendar: 'calendar',
  sales: 'sales',
  retention: 'retention',
  inbox: 'inbox',
  d2c: 'd2c',
  vault: 'vault',
  webchanges: 'webchanges',
};
const NB_ROUTE_FROM_VIEW = Object.fromEntries(
  Object.entries(NB_VIEW_FROM_ROUTE).map(([nb, v]) => [v, nb]),
);

// Keeps the current page in the URL (?page=finance) so refresh, back/forward,
// and shared links land on the page you were actually on instead of always
// bouncing to the default landing view.
function viewFromLocation() {
  const page = new URLSearchParams(window.location.search).get('page');
  if (!page) return 'forecaster';
  return NB_VIEW_FROM_ROUTE[page] || page;
}
const NB_ITEMS = [
  // hiddenFromNav: true hides the chip from the sidebar but leaves the route
  // intact — `view === 'home'` etc. still render their components, so direct
  // URL/code-driven navigation and the default landing view keep working.
  // Reversible: flip the flag or delete it. (Mo, 2026-05-26.)
  { key: 'home',      label: 'Home',      icon: 'home',     perm: 'home',      universal: true, hiddenFromNav: true },
  { key: 'archive',   label: 'Archive',   icon: 'reports',  perm: 'archive',   universal: true, hiddenFromNav: true },
  { key: 'inbox',     label: 'Inbox',     icon: 'reports',  perm: 'reporting', wip: true, adminOnly: true, hiddenFromNav: true },
  { key: 'sales',     label: 'Sales',     icon: 'reports',  perm: 'sales' },
  { key: 'stock',     label: 'Stock',     icon: 'stock',    perm: 'stock' },
  { key: 'finance',   label: 'Finance',   icon: 'finance',  perm: 'finance' },
  // B2B / Accounts Receivable. Team-visible (no adminOnly/wip) → all staff see it
  // unless an admin sets session.permissions.b2b === false. (Re-introduced 2026-06-08;
  // an earlier B2B surface was removed 2026-05-08 — different backing tables.)
  { key: 'b2b',       label: 'B2B',       icon: 'finance',  perm: 'b2b' },
  { key: 'd2c',       label: 'D2C',       icon: 'reports',  perm: 'd2c' },
  { key: 'contacts',  label: 'Contacts',  icon: 'team',     perm: 'contacts' },
  { key: 'reporting', label: 'Reporting', icon: 'reports',  perm: 'reporting', hiddenFromNav: true },
  { key: 'clouseau',  label: 'Clouseau',  icon: 'eyes',     perm: 'reporting', wip: true },
  { key: 'files',     label: 'Files',     icon: 'reports',  perm: 'files',     wip: true },
  { key: 'calendar',  label: 'Calendar',  icon: 'calendar', perm: 'calendar',  wip: true },
  { key: 'retention', label: 'Retention', icon: 'team',     perm: 'retention', wip: true },
  // Password Vault — company credentials + access log. Any staff member
  // with the `vault` permission can browse/create/edit; reveal is
  // admin-only and enforced server-side (vault-reveal Lambda). Standalone
  // page per Renz's explicit request, not nested in Admin Panel. (2026-07-16.)
  { key: 'vault',     label: 'Vault',     icon: 'vault',    perm: 'vault',     wip: true, graduated: true },
  // Multi-project website changelog + activity log. Any staff with the
  // `webchanges` permission can browse/log; adding projects is admin-only
  // (enforced in-page). Opt-in like the other new pages. (2026-07-21.)
  { key: 'webchanges', label: 'Web Changes', icon: 'reports', perm: 'webchanges', wip: true, graduated: true },
];

export default function App() {
  const [session, setSession] = useState(null);
  const [booted, setBooted] = useState(false);
  // Default landing changed 2026-05-26 from 'home' to 'forecaster' as part
  // of the Home/Inbox/Reporting hide pass. Home route stays intact and is
  // still the !isViewAllowed fallback for users without forecaster perms.
  const [view, setView] = useState(() => viewFromLocation());
  const [adminOpen, setAdminOpen] = useState(false);
  const [signalsOpen, setSignalsOpen] = useState(false);
  const [openTaskId, setOpenTaskId] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteStaff, setPaletteStaff] = useState([]);

  // Master-hide page keys an admin has toggled off via Admin Panel → Visibility.
  const [hiddenPages, setHiddenPages] = useState(() => new Set());
  useEffect(() => {
    let cancelled = false;
    async function loadHidden() {
      try {
        const { data } = await aws.from('master_hidden_pages').select('page_key');
        if (!cancelled) setHiddenPages(new Set((data || []).map(r => r.page_key)));
      } catch {
        if (!cancelled) setHiddenPages(new Set());
      }
    }
    loadHidden();
    function onChange() { loadHidden(); }
    window.addEventListener('fon-pages-visibility-changed', onChange);
    return () => { cancelled = true; window.removeEventListener('fon-pages-visibility-changed', onChange); };
  }, []);

  const isViewAllowed = useMemo(() => {
    if (!session) return false;
    const navKey = NB_ROUTE_FROM_VIEW[view] || view;
    if (navKey !== 'home' && navKey !== 'myday' && hiddenPages.has(navKey)) return false;
    if (session.role === 'admin') return true;
    if (view === 'myday') return true;
    const item = NB_ITEMS.find(it => it.key === navKey);
    if (!item) return false;
    if (item.adminOnly) return false;
    if (item.universal) return true;
    if (item.wip) return session.permissions?.[item.perm] === true;
    return session.permissions?.[item.perm] !== false;
  }, [view, session, hiddenPages]);
  useEffect(() => {
    if (session && !isViewAllowed) setView('home');
  }, [session, isViewAllowed]);

  useEffect(() => {
    if (!session) return;
    const navKey = NB_ROUTE_FROM_VIEW[view] || view;
    const params = new URLSearchParams(window.location.search);
    if (params.get('page') === navKey) return;
    params.set('page', navKey);
    window.history.pushState({ page: navKey }, '', `${window.location.pathname}?${params.toString()}`);
  }, [view, session]);

  useEffect(() => {
    function onPopState() { setView(viewFromLocation()); }
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    function onKey(e) {
      // Cmd+K still opens command palette. Cmd+M intentionally removed
      // 2026-05-08 — Slack is the only way to talk to Harry.
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setPaletteOpen(o => !o); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // G-then navigation shortcuts
  useEffect(() => {
    let gPressed = false;
    let gTimer = null;
    // 2026-05-26: 'h' removed since Home is now hidden from the main nav.
    const G_MAP = { s: 'forecaster', f: 'finance', p: 'contacts', k: 'files', a: 'archive' };
    function onKey(e) {
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'g' && !gPressed) {
        gPressed = true;
        clearTimeout(gTimer);
        gTimer = setTimeout(() => { gPressed = false; }, 800);
        return;
      }
      if (gPressed) {
        const dest = G_MAP[e.key.toLowerCase()];
        if (dest) { e.preventDefault(); setView(dest); }
        gPressed = false;
        clearTimeout(gTimer);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); clearTimeout(gTimer); };
  }, []);

  // Load staff for command palette (on open). Tasks query removed in
  // 2026-05-08 cleanup along with the tasks table.
  //
  // Mig 139 (C1 from 2026-05-10 audit): staff_directory no longer exposes
  // `code` (it IS the JWT sub claim). For admins we need codes for the
  // "Switch to X" impersonation action, so we call the JWT-gated
  // admin_staff_directory() RPC. For non-admins we use the safe view —
  // they only get name+role for fuzzy-matching.
  useEffect(() => {
    if (!paletteOpen || !session) return;
    if (session?.role === 'admin') {
      aws.rpc('admin_staff_directory')
        .then(({ data }) => setPaletteStaff((data || []).filter(s => s.active)));
    } else {
      aws.from('staff_directory').select('name, role').eq('active', true)
        .then(({ data }) => setPaletteStaff(data || []));
    }
  }, [paletteOpen, session]);

  useEffect(() => {
    const stored = readStoredSession();
    const jwtOk = stored && stored.jwt && stored.jwt_exp_ms && Date.now() < stored.jwt_exp_ms;
    if (stored && jwtOk) {
      setJwt(stored.jwt);
      setActor(stored).finally(() => {
        setSession(stored);
        setBooted(true);
        if (stored.role === 'admin' && new URLSearchParams(window.location.search).get('welcome') === '1') setShowWelcome(true);
      });
    } else {
      if (stored) clearStoredSession();
      setJwt(null);
      setBooted(true);
    }
  }, []);

  if (!booted) return null;

  if (!session) return <NebulaLandingAuth onAuthenticated={(s) => { setSession(s); if (s.first_login || (s.role === 'admin' && new URLSearchParams(window.location.search).get('welcome') === '1')) setShowWelcome(true); }} />;

  if (showWelcome) return <Welcome session={session} onDismiss={() => setShowWelcome(false)} />;

  const signOut = () => { clearStoredSession(); setJwt(null); setSession(null); };

  return (
    <ToastProvider>
    <PageContextProvider>
    <div className="app-shell app-shell--nb">
      <NavBar
        active={NB_ROUTE_FROM_VIEW[view] || view}
        user={{
          name: session?.name || 'Mo',
          role: session?.role === 'admin' ? 'Super-Admin' : 'User',
          hue: 158,
        }}
        navItems={NB_ITEMS.filter(it => {
          if (it.hiddenFromNav) return false;
          if (it.key !== 'home' && hiddenPages.has(it.key)) return false;
          if (it.adminOnly) return session?.role === 'admin';
          if (it.universal) return true;
          if (session?.role === 'admin') return true;
          if (it.wip) return session?.permissions?.[it.perm] === true;
          return session?.permissions?.[it.perm] !== false;
        })}
        onNavigate={(key) => setView(NB_VIEW_FROM_ROUTE[key] || key)}
        onUserClick={session?.role === 'admin' ? () => setAdminOpen(true) : undefined}
        onSignalsClick={session?.role === 'admin' ? () => setSignalsOpen(true) : undefined}
        onLogout={signOut}
      />
      <div className="app-main">
        <HealthBanner />
        {!isViewAllowed && <Home session={session} />}
        {isViewAllowed && view === 'home' && <Home session={session} />}
        {isViewAllowed && view === 'calendar' && <CalendarPage />}
        {isViewAllowed && view === 'myday' && <Home session={session} />}
        {isViewAllowed && view === 'signals' && <Signals />}
        {isViewAllowed && view === 'sales' && <Sales session={session} />}
        {isViewAllowed && view === 'finance' && <Finance session={session} />}
        {isViewAllowed && view === 'b2b' && <B2B session={session} />}
        {isViewAllowed && view === 'd2c' && <D2CSales session={session} />}
        {isViewAllowed && view === 'retention' && <RetentionTab />}
        {isViewAllowed && view === 'files' && <FilesTab />}
        {isViewAllowed && view === 'contacts' && <AddressBook session={session} onSignOut={signOut} />}
        {isViewAllowed && view === 'forecaster' && <Forecaster session={session} />}
        {isViewAllowed && view === 'reporting' && <Reporting session={session} />}
        {isViewAllowed && view === 'inbox' && <Inbox session={session} />}
        {isViewAllowed && view === 'clouseau' && <Clouseau session={session} />}
        {isViewAllowed && view === 'archive' && <Archive session={session} onSignOut={signOut} />}
        {isViewAllowed && view === 'vault' && <VaultPage session={session} />}
        {isViewAllowed && view === 'webchanges' && <WebChanges session={session} />}
      </div>
      {adminOpen && session.role === 'admin' && <AdminPanel session={session} onClose={() => setAdminOpen(false)} />}
      {signalsOpen && session.role === 'admin' && <SystemSignalsModal session={session} onClose={() => setSignalsOpen(false)} />}
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        session={session}
        staff={paletteStaff}
        tasks={[]}
        onNavigate={(dest, action, payload) => {
          if (dest) setView(dest);
          if (action === 'shutdown') { setView('home'); setTimeout(() => window.dispatchEvent(new CustomEvent('fon-shutdown')), 100); }
          if (action?.startsWith('switch-')) { setView('home'); }
        }}
      />
    </div>
    </PageContextProvider>
    </ToastProvider>
  );
}
